import * as Sentry from '@sentry/react';
import { useEffect, type ReactElement } from 'react';

import { ConnectSlackModal } from '@/components/slack/ConnectSlackModal';
import { DisconnectSlackDialog } from '@/components/slack/DisconnectSlackDialog';
import { getErrorMessage } from '@/hooks/api/errorHelpers';
import {
  useDeleteOrganizationSlackSettings,
  useOrganizationSlackSettings,
  useUpsertOrganizationSlackSettings,
} from '@/hooks/api/useSlackSettings';
import type {
  GrantedIntegrationAccess,
  RenderableIntegrationAccess,
} from '@/hooks/useIntegrationAccess';
import type { slackSettings } from '@kirin/api-client/contracts';

import { SlackIntegrationCard } from './SlackIntegrationCard';
import { useSlackIntegrationController } from './useSlackIntegrationController';

function isPermissionDeniedError(error: unknown): boolean {
  return (
    error != null &&
    typeof error === 'object' &&
    'status' in error &&
    (error as { status: unknown }).status === 403
  );
}

const SLACK_RBAC_MESSAGE =
  "You don't have permission to view Slack settings. Contact your administrator.";

function deriveSlackError(
  query: { isError: boolean; error: unknown },
  is403: boolean
): string | undefined {
  if (is403) return SLACK_RBAC_MESSAGE;
  if (!query.isError) return undefined;
  return getErrorMessage(query.error);
}

interface SlackIntegrationProps {
  access: RenderableIntegrationAccess;
}

export function SlackIntegration({
  access,
}: SlackIntegrationProps): ReactElement {
  if (access.canRender) return <SlackIntegrationInner access={access} />;
  return <SlackIntegrationCard settings={null} isLoading />;
}

SlackIntegration.displayName = 'SlackIntegration';

function SlackIntegrationInner({
  access,
}: {
  access: GrantedIntegrationAccess;
}): ReactElement | null {
  const { organizationId, isLoading: isAccessLoading } = access;

  const slackQuery = useOrganizationSlackSettings(organizationId);
  const upsertMutation = useUpsertOrganizationSlackSettings(organizationId);
  const deleteMutation = useDeleteOrganizationSlackSettings(organizationId);

  const controller = useSlackIntegrationController({
    saveSettings: (
      data: slackSettings.UpsertSlackSettingsBody
    ): Promise<slackSettings.UpsertSlackSettingsResponse> =>
      upsertMutation.mutateAsync(data),
    deleteSettings: (): Promise<unknown> => deleteMutation.mutateAsync(),
    sentryTags: { save: 'slack_save', delete: 'slack_delete' },
    sentryExtra: { organizationId },
  });

  const is403 = slackQuery.isError && isPermissionDeniedError(slackQuery.error);
  const safeSettings = is403 ? null : slackQuery.data;
  const slackError = deriveSlackError(slackQuery, is403);

  useEffect(() => {
    // 403 = RBAC, not an exception — surface as user-facing message only.
    if (is403 || slackError === undefined) return;
    Sentry.captureException(slackQuery.error, {
      tags: { error_type: 'slack_settings_fetch' },
      extra: { organizationId },
    });
  }, [slackQuery.error, slackError, is403, organizationId]);

  const isCardLoading = isAccessLoading || slackQuery.isLoading;

  return (
    <>
      <SlackIntegrationCard
        settings={safeSettings}
        onConnect={controller.openConnect}
        onManage={controller.openConnect}
        onDisconnect={controller.openDisconnect}
        isLoading={isCardLoading}
        error={slackError}
      />
      {controller.connectOpen && (
        <ConnectSlackModal
          key={organizationId}
          open={controller.connectOpen}
          onOpenChange={controller.setConnectOpen}
          scopeId={organizationId}
          settings={safeSettings}
          isLoading={slackQuery.isLoading}
          onSave={controller.handleSave}
          onRequestDisconnect={controller.handleRequestDisconnect}
          readOnly={!access.canWrite}
        />
      )}
      {controller.disconnectOpen && (
        <DisconnectSlackDialog
          isOpen={controller.disconnectOpen}
          onClose={controller.closeDisconnect}
          onConfirm={controller.handleConfirmDisconnect}
          isLoading={deleteMutation.isPending}
          readOnly={!access.canWrite}
        />
      )}
    </>
  );
}

SlackIntegrationInner.displayName = 'SlackIntegrationInner';
